import React from 'react';
import './ComplianceCard.css';

interface ComplianceData {
  compliance_pct: number | null;
  basis: string;
  planned: { duration_s: number | null; tss: number | null; target_zone: string | null };
  actual: {
    duration_s: number | null; tss: number | null; distance_km: number | null;
    avg_power: number | null; avg_hr: number | null; np_watts: number | null;
    activity_name: string | null; activity_id: number | null;
  };
  diffs: { duration_pct?: number; tss_pct?: number };
}

function formatTime(seconds: number | null): string {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}` : `${m}:${s.toString().padStart(2, '0')}`;
}

function diffClass(val: number | undefined): string {
  if (val === undefined) return 'compliance-diff-neutral';
  if (val > 0) return 'compliance-diff-positive';
  if (val < 0) return 'compliance-diff-negative';
  return 'compliance-diff-neutral';
}

function formatDiff(val: number | undefined): string {
  if (val === undefined) return '—';
  return `${val > 0 ? '+' : ''}${val}%`;
}

export default function ComplianceCard({ compliance }: { compliance: ComplianceData }) {
  const pct = compliance.compliance_pct;
  const markerPos = pct ? Math.min(pct, 150) / 1.5 : 50;
  const pctColor = pct && pct >= 85 && pct <= 115 ? '#5cb85c' : pct && pct > 115 ? '#f0ad4e' : '#d9534f';

  return (
    <div className="compliance-card">
      <div className="compliance-header">Compliance</div>

      <div className="compliance-bar-wrap">
        <div className="compliance-bar-bg">
          <div className="compliance-bar-gradient" />
          <div className="compliance-marker" style={{ left: `${markerPos}%` }} />
        </div>
      </div>

      {pct !== null && (
        <>
          <div className="compliance-pct" style={{ color: pctColor }}>{pct}%</div>
          <div className="compliance-basis">Based on {compliance.basis === 'duration' ? 'duration' : compliance.basis}</div>
        </>
      )}

      <table className="compliance-table">
        <thead>
          <tr><th>Metric</th><th>Plan</th><th>Actual</th><th>Diff</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Duration</td>
            <td>{formatTime(compliance.planned.duration_s)}</td>
            <td>{formatTime(compliance.actual.duration_s)}</td>
            <td className={diffClass(compliance.diffs.duration_pct)}>{formatDiff(compliance.diffs.duration_pct)}</td>
          </tr>
          <tr>
            <td>TSS</td>
            <td>{compliance.planned.tss ?? '—'}</td>
            <td>{compliance.actual.tss != null ? Math.round(compliance.actual.tss) : '—'}</td>
            <td className={diffClass(compliance.diffs.tss_pct)}>{formatDiff(compliance.diffs.tss_pct)}</td>
          </tr>
          <tr>
            <td>Distance</td>
            <td>—</td>
            <td>{compliance.actual.distance_km ? `${compliance.actual.distance_km} km` : '—'}</td>
            <td className="compliance-diff-neutral">—</td>
          </tr>
          {compliance.actual.avg_power != null && (
            <tr>
              <td>Avg Power</td>
              <td>{compliance.planned.target_zone ? compliance.planned.target_zone.replace('zone', 'Z') : '—'}</td>
              <td>{Math.round(compliance.actual.avg_power)}W</td>
              <td className="compliance-diff-neutral">—</td>
            </tr>
          )}
          {compliance.actual.np_watts != null && (
            <tr>
              <td>NP</td>
              <td>—</td>
              <td>{Math.round(compliance.actual.np_watts)}W</td>
              <td className="compliance-diff-neutral">—</td>
            </tr>
          )}
          {compliance.actual.avg_hr != null && (
            <tr>
              <td>Avg HR</td>
              <td>—</td>
              <td>{Math.round(compliance.actual.avg_hr)} bpm</td>
              <td className="compliance-diff-neutral">—</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
